import React, { useState, useEffect, useRef } from 'react';

interface DateInputDDMMYYYYProps {
  value: string; // ISO format yyyy-mm-dd
  onChange: (value: string) => void;
  disabled?: boolean;
  required?: boolean;
  style?: React.CSSProperties;
  name?: string;
  min?: string;
  max?: string;
}

/**
 * Date input that displays dd/mm/yyyy format.
 * Internally stores and emits ISO yyyy-mm-dd values.
 * Uses a hidden native date picker for the calendar popup.
 */
export function DateInputDDMMYYYY({
  value,
  onChange,
  disabled = false,
  required = false,
  style,
  name,
  min,
  max,
}: DateInputDDMMYYYYProps) {
  const hiddenInputRef = useRef<HTMLInputElement>(null);
  const [displayValue, setDisplayValue] = useState('');

  useEffect(() => {
    if (value) {
      const parts = value.split('-');
      if (parts.length === 3) {
        setDisplayValue(`${parts[1]}/${parts[2]}/${parts[0]}`);
      } else {
        setDisplayValue('');
      }
    } else {
      setDisplayValue('');
    }
  }, [value]);

  function handleDisplayChange(e: React.ChangeEvent<HTMLInputElement>) {
    const text = e.target.value;
    // Allow typing with auto-slash insertion
    let cleaned = text.replace(/[^0-9/]/g, '');
    
    // Auto-insert slashes
    const digits = cleaned.replace(/\//g, '');
    if (digits.length <= 2) {
      cleaned = digits;
    } else if (digits.length <= 4) {
      cleaned = `${digits.slice(0, 2)}/${digits.slice(2)}`;
    } else {
      cleaned = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
    }

    setDisplayValue(cleaned);

    // Parse mm/dd/yyyy to yyyy-mm-dd
    const match = cleaned.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (match) {
      const [, mm, dd, yyyy] = match;
      const day = parseInt(dd, 10);
      const month = parseInt(mm, 10);
      const year = parseInt(yyyy, 10);
      if (month >= 1 && month <= 12 && day >= 1 && day <= 31 && year >= 1900) {
        onChange(`${yyyy}-${mm}-${dd}`);
      }
    } else if (cleaned === '') {
      onChange('');
    }
  }

  function handleCalendarClick() {
    if (!disabled && hiddenInputRef.current) {
      hiddenInputRef.current.showPicker();
    }
  }

  function handleNativeDateChange(e: React.ChangeEvent<HTMLInputElement>) {
    onChange(e.target.value);
  }

  const baseStyle: React.CSSProperties = {
    width: '100%',
    padding: '8px 36px 8px 12px',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    fontSize: '14px',
    boxSizing: 'border-box',
    outline: 'none',
    backgroundColor: disabled ? '#f9fafb' : '#fff',
    cursor: disabled ? 'not-allowed' : 'text',
    ...style,
  };

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <input
        type="text"
        value={displayValue}
        onChange={handleDisplayChange}
        placeholder="mm/dd/yyyy"
        disabled={disabled}
        required={required}
        name={name}
        maxLength={10}
        style={baseStyle}
        onFocus={(e) => {
          if (style?.boxShadow === undefined) {
            e.target.style.boxShadow = '0 0 0 3px rgba(31, 64, 175, 0.1)';
          }
        }}
        onBlur={(e) => {
          e.target.style.boxShadow = 'none';
        }}
      />
      {/* Calendar icon button */}
      <button
        type="button"
        onClick={handleCalendarClick}
        disabled={disabled}
        style={{
          position: 'absolute',
          right: '8px',
          top: '50%',
          transform: 'translateY(-50%)',
          background: 'none',
          border: 'none',
          cursor: disabled ? 'not-allowed' : 'pointer',
          padding: '2px',
          fontSize: '16px',
          color: '#6b7280',
        }}
        tabIndex={-1}
        aria-label="Open date picker"
      >
        📅
      </button>
      {/* Hidden native date input for calendar popup */}
      <input
        ref={hiddenInputRef}
        type="date"
        value={value}
        onChange={handleNativeDateChange}
        min={min}
        max={max}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          opacity: 0,
          pointerEvents: 'none',
        }}
        tabIndex={-1}
      />
    </div>
  );
}
